import React, { Fragment, useState, useEffect, useRef } from 'react';
import { useFormContext, Controller, ErrorMessage } from 'react-hook-form';
import { Grid, TextField, Button, FormControl, Select, MenuItem, InputLabel } from '@material-ui/core';
import { useStyle } from './style';
import errormesages from './errormessages';
import { EIRCODE_FINDER_URL, EIRCODE_LOGO_IMAGE_PATH } from './constants';
import { useApolloClient } from '@apollo/client';
import ENDPOINT from '../graphql/GraphQLConstants';
import Loading from '../shared/Loading';

export const EirAddressFinder = ({ isDeliveryAddress }) => {
	const { register, errors, control, setValue, setError, clearError, getValues } = useFormContext();
	const graphql = useApolloClient();
	const classes = useStyle();
	const prefixAddress = isDeliveryAddress ? 'deliveryAddress' : 'billingAddress';
	const { validation, errormessage } = errormesages;
	const [isAddressLoading, setIsAddressLoading] = useState(false);
	const [addressList, setAddressList] = useState([]);
	const [eirCode, setEirCode] = useState('');
	const [eirCodeEmpty, setEirCodeEmpty] = useState(true);
	const [showAddress, setShowAddress] = useState(false);
	const eirCodeInputRef = useRef();

	useEffect(() => {
		const addressLine1 = getValues(`${prefixAddress}addressLine1`);
		const addressLine2 = getValues(`${prefixAddress}addressLine2`);
		const addressLine3 = getValues(`${prefixAddress}addressLine3`);
		const town = getValues(`${prefixAddress}town`);
		const county = getValues(`${prefixAddress}county`);

		if (addressLine1.length != 0) {
			setTimeout(() => {
				setShowAddress(true);
				let addressLine = `${splitAddress(addressLine1)}\n`;
				addressLine = addressLine2.length != 0 ? `${addressLine}${splitAddress(addressLine2)}\n` : addressLine;
				addressLine = addressLine3.length != 0 ? `${addressLine}${splitAddress(addressLine3)}\n` : addressLine;
				let countyValue = '';
				county.split('_').map((countyVal, index) => {
					index == 0 ? countyValue = countyValue + toSentenceCase(countyVal) + '. ' : countyValue = countyValue + toSentenceCase(countyVal);
				});
				addressLine = `${addressLine}${toSentenceCase(town)}\n${countyValue}`;
				setValue(`${prefixAddress}yourAddress`, addressLine);
			}, 300);
		}

	}, []);


	const handleEirCodeChange = (event) => {
		const eirCodeValue = event.target.value;
		setEirCode(eirCodeValue.replace(/ /g, ''));
		setAddressList([]);
		setShowAddress(false);
		clearError(`${prefixAddress}EirCode`);
		if (event.target.value.trim().length != 0) {
			setEirCodeEmpty(false);
		}
		else {
			setEirCodeEmpty(true);
		}
	}
	const handleOnClickEircodeConfirm = () => {
		if (eirCode.trim().length > 0) {
			clearError(`${prefixAddress}EirCode`);
			getAddressList();
		}
	}
	const toSentenceCase = (input) => {
		let inputLowerCase = input.toLowerCase();
		return `${inputLowerCase.charAt(0).toUpperCase()}${inputLowerCase.slice(1)}`;
	}
	const splitAddress = (address) => {
		let newAddress = '';
		address.split(' ').map((addressVal) => {
			newAddress = newAddress + toSentenceCase(addressVal) + ' ';
		});
		return newAddress.trim();
	}
	const setAddressFields = (address) => {
		setTimeout(() => {
			setShowAddress(true);
			let addressLine = `${splitAddress(address.addressLine1)}\n`;
			addressLine = address.addressLine2 != null ? `${addressLine}${splitAddress(address.addressLine2)}\n` : addressLine;
			addressLine = address.addressLine3 != null ? `${addressLine}${splitAddress(address.addressLine3)}\n` : addressLine;
			let county = '';
			address.county.split('_').map((countyVal, index) => {
				index == 0 ? county = county + toSentenceCase(countyVal) + '. ' : county = county + toSentenceCase(countyVal);
			});
			addressLine = `${addressLine}${toSentenceCase(address.town)}\n${county}`;
			setValue(`${prefixAddress}yourAddress`, addressLine, { shouldDirty: true });
			setValue(`${prefixAddress}addressLine1`, address.addressLine1, { shouldDirty: true });
			setValue(`${prefixAddress}addressLine2`, address.addressLine2, { shouldDirty: true });
			setValue(`${prefixAddress}addressLine3`, address.addressLine3, { shouldDirty: true });
			setValue(`${prefixAddress}town`, address.town, { shouldDirty: true });
			setValue(`${prefixAddress}county`, address.county, { shouldDirty: true });
		}, 300);
	}

	const getAddressList = async () => {
		setIsAddressLoading(true);
		try {
			const response = await graphql.query({
				query: ENDPOINT.GET_ADDRESSLIST,
				variables: {
					eirCode: eirCode
				},
			});

			let data = response.data.addressList.addresses;
			setAddressList(data);
			if (data.length == 0) {
				//No address found for this Eircode
				setError(`${prefixAddress}EirCode`, 'manual', errormessage.noAddressFoundforEirCode);
			}
			else if (data.length == 1) {
				setAddressFields(data[0]);
			}

		}
		catch (err) {
			setError(`${prefixAddress}EirCode`, 'manual', errormessage.addressFinderNetworkError);
		}
		finally {
			setIsAddressLoading(false);
		}
	}


	return (
		<Fragment>
			<Grid container spacing={2}>
				<Grid item xs={12} sm={6}>
					<Controller
						as={<TextField inputRef={eirCodeInputRef} />}
						control={control}
						fullWidth
						id={prefixAddress + 'EirCode'}
						name={prefixAddress + 'EirCode'}
						label="Eircode"
						variant="outlined"
						size="small"
						required
						rules={{
							required: { value: true, message: validation.eircodeRequired }
						}}
						error={isDeliveryAddress ? Boolean(errors.deliveryAddressEirCode) : Boolean(errors.billingAddressEirCode)}
						onChange={([event]) => {
							handleEirCodeChange(event);
							return event.target.value;
						}}
						onFocus={() => {
							eirCodeInputRef.current.focus();
						}}
					/>
					<ErrorMessage errors={errors} name={`${prefixAddress}EirCode`}>
						{({ message }) => <p className={classes.errorMessage}> {message}</p>}
					</ErrorMessage>

				</Grid>
				{!isAddressLoading && (
					<Grid item xs={12} sm={6}>
						<Grid container justify='flex-end'>
							<Grid item>
								<a href={EIRCODE_FINDER_URL} rel="noopener noreferrer" target="_blank" className={classes.eirCodeLink}>
									<Button variant="outlined" className={classes.button} color="primary">Find your <img className={classes.eirCodeLogo} src={EIRCODE_LOGO_IMAGE_PATH} /></Button>
								</a>
								<Button variant="contained" className={classes.greybutton} disabled={eirCodeEmpty} color={eirCodeEmpty ? 'default' : 'primary'}
									onClick={(event) => { handleOnClickEircodeConfirm(event) }}>
									Confirm eircode
						</Button>
							</Grid>
						</Grid>
					</Grid>
				)}
				{isAddressLoading && (
					<Grid item xs={12}>
						<Loading />
					</Grid>
				)}
				<Grid item xs={12} sm={6}>
					{addressList.length > 1 && (
						<FormControl variant="outlined" fullWidth size="small">
							<InputLabel required>Please select your address</InputLabel>
							<Controller
								control={control}
								name={prefixAddress + 'addressSelect'}
								id={prefixAddress + 'addressSelect'}
								inputRef={register}
								defaultValue=""
								rules={{ required: { value: true, message: validation.addressSelectRequired } }}
								error={isDeliveryAddress ? Boolean(errors.deliveryAddressaddressSelect) : Boolean(errors.billingAddressaddressSelect)}
								as={
									<Select
										id={prefixAddress + 'addresses'}
										label="Please select your address *"
									>
										{
											addressList.map((address, index) => (
												<MenuItem key={index} value={index} className={classes.selectLabel}>
													{`${address.addressLine1} - ${address.town} - ${address.county}`}
												</MenuItem>
											))}
									</Select>
								}
								onChange={([event]) => {
									setAddressFields(addressList[parseInt(event.target.value)]);
									return event.target.value;
								}}

							/>
							<ErrorMessage errors={errors} name={prefixAddress + 'addressSelect'}>
								{({ message }) => <p className={classes.errorMessage}>{message}</p>}
							</ErrorMessage>
						</FormControl>
					)}
				</Grid>

				{showAddress && (
					<Grid item xs={12}>
						<Grid container spacing={2}>
							<Grid item xs={12} sm={6}>
								<Controller
									as={TextField}
									control={control}
									fullWidth
									InputLabelProps={{ shrink: true }}
									id={prefixAddress + 'yourAddress'}
									name={prefixAddress + 'yourAddress'}
									label="Your address"
									variant="outlined"
									size="small"
									inputRef={register}
									disabled
									multiline
									rowsMax={6}
								/>
							</Grid>
						</Grid>
					</Grid>
				)}
				<input type="hidden" ref={register} id={prefixAddress + 'addressLine1'} name={prefixAddress + 'addressLine1'} />
				<input type="hidden" ref={register} id={prefixAddress + 'addressLine2'} name={prefixAddress + 'addressLine2'} />
				<input type="hidden" ref={register} id={prefixAddress + 'addressLine3'} name={prefixAddress + 'addressLine3'} />
				<input type="hidden" ref={register} id={prefixAddress + 'town'} name={prefixAddress + 'town'} />
				<input type="hidden" ref={register} id={prefixAddress + 'county'} name={prefixAddress + 'county'} />
			</Grid>
		</Fragment>
	);


}
